import React, { useMemo } from 'react';

import { type BackgroundColor, Box, Text, xcss } from '@atlaskit/primitives';

const MIN_VALUE = 0;
const MAX_VALUE = 1;

const containerStyles = xcss({
	height: '12px',
	width: '100%',
	borderRadius: 'border.radius.circle',
	position: 'relative',
});

const barStyles = xcss({
	minWidth: '12px',
	height: '12px',
	borderRadius: 'border.radius.circle',
});

const baseBarStyles = xcss({
	minWidth: '12px',
	height: '12px',
	borderTopLeftRadius: 'border.radius.circle',
	borderBottomLeftRadius: 'border.radius.circle',
	backgroundColor: 'color.background.accent.purple.subtle',
	position: 'absolute',
	top: 'space.0',
	left: 'space.0',
	borderRightWidth: 'border.width.outline',
	borderRightStyle: 'solid',
	borderRightColor: 'color.border.inverse',
});

const baseTextStyles = xcss({
	width: '100%',
	minWidth: '12px',
	marginLeft: 'space.150',
	position: 'relative',

	'::after': {
		borderLeftWidth: 'border.width.outline',
		borderLeftStyle: 'dotted',
		borderLeftColor: 'color.border.discovery',
		position: 'absolute',
		top: '0',
		left: '-2px',
		content: '""',
		display: 'block',
		width: '1px',
		height: '16px',
	},
});

type ProgressBarProps = {
	/**
	 * This is the descriptive label that's associated with the progress bar.
	 * Always include useful information on the current state of the progress bar so that people who use assistive technology can understand what the current state of the progress bar is.
	 */
	ariaLabel?: string;
	/** Sets the value of the progress bar, between `0` and `1` inclusive. */
	value: number;
	/**
	 * This is to show the base limit of a storage plan
	 */
	baseLimit?: {
		limit: number;
		title: string;
	};
	limit: number;
};

export const ProgressBar = ({ value, ariaLabel, baseLimit, limit }: ProgressBarProps) => {
	// Handle instances where we're passed 140% for overages
	const amount = Math.max(MIN_VALUE, Math.min(value, MAX_VALUE));

	const backgroundColor = useMemo((): BackgroundColor => {
		if (baseLimit !== undefined) {
			return 'color.background.accent.blue.bolder';
		}
		if (amount >= 0.9) {
			return 'color.background.danger.bold';
		}

		if (amount >= 0.7) {
			return 'color.background.warning.bold';
		}

		return 'color.background.accent.blue.bolder';
	}, [amount, baseLimit]);

	if (baseLimit !== undefined) {
		return (
			<Box
				backgroundColor={'color.background.input'}
				xcss={containerStyles}
				role="progressbar"
				aria-label={ariaLabel}
				aria-valuemin={MIN_VALUE}
				aria-valuenow={amount}
				aria-valuemax={MAX_VALUE}
			>
				<Box
					backgroundColor={backgroundColor}
					style={{ width: `${Number(amount) * 100}%` }}
					xcss={barStyles}
				/>

				<Box>
					<Box
						style={{ width: `${Number(baseLimit.limit / limit) * 100}%` }}
						xcss={baseBarStyles}
					/>
					<Box
						style={{ paddingLeft: `${Number(baseLimit.limit / limit) * 100}%` }}
						xcss={baseTextStyles}
					>
						<Text size="small" weight="medium" color="color.text.discovery">
							{baseLimit.title}
						</Text>
					</Box>
				</Box>
			</Box>
		);
	}
	return (
		<Box
			backgroundColor={'color.background.accent.gray.subtlest'}
			xcss={containerStyles}
			role="progressbar"
			aria-label={ariaLabel}
			aria-valuemin={MIN_VALUE}
			aria-valuenow={amount}
			aria-valuemax={MAX_VALUE}
		>
			<Box
				backgroundColor={backgroundColor}
				style={{ width: `${Number(amount) * 100}%` }}
				xcss={barStyles}
			/>
		</Box>
	);
};
